import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api';
import { useAuth, isAdminLike } from '../context/AuthContext';
import { styles, fmtMoney, fmtDateTime, useIsMobile, mobile } from '../components/UI';

// Browse all accounts (customers + project sites). Click one to drill in.
export default function Accounts() {
  const { user } = useAuth();
  const showAmFilter = isAdminLike(user) || user?.role === 'supervisor';
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState('');
  const [amFilter, setAmFilter] = useState('');
  const [healthFilter, setHealthFilter] = useState('');
  const [projectsFilter, setProjectsFilter] = useState('');
  const [ageFilter, setAgeFilter] = useState('');
  const [error, setError] = useState('');
  const isMobile = useIsMobile();

  useEffect(() => {
    api.listAccounts().then(setRows).catch(e => setError(e.message));
  }, []);

  // Distinct rep names across all visible rows -- powers the AM dropdown.
  // Derived client-side so we don't need an extra API call and only show
  // reps who actually have accounts in the caller's scope.
  const amOptions = useMemo(() => {
    const set = new Set();
    rows.forEach(r => {
      if (r.last_contact_rep_name) set.add(r.last_contact_rep_name);
      (r.rep_names || []).forEach(n => n && set.add(n));
    });
    return Array.from(set).sort();
  }, [rows]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const now = Date.now();
    const ageDays = ageFilter ? Number(ageFilter) : null;
    return rows.filter(r => {
      if (q && !r.name.toLowerCase().includes(q)) return false;
      if (amFilter) {
        const reps = r.rep_names || [];
        if (!reps.includes(amFilter)) return false;
      }
      if (healthFilter === 'stale' && !r.stale) return false;
      if (healthFilter === 'active' && r.stale) return false;
      if (projectsFilter === 'with' && (r.project_count || 0) === 0) return false;
      if (projectsFilter === 'without' && (r.project_count || 0) > 0) return false;
      if (ageDays !== null) {
        const ts = r.last_contact_at ? new Date(r.last_contact_at).getTime() : 0;
        const ageMs = ageDays * 24 * 60 * 60 * 1000;
        if (ageDays > 0 && (now - ts) > ageMs) return false;          // within last N days
        if (ageDays < 0 && (now - ts) <= Math.abs(ageDays) * 24 * 60 * 60 * 1000) return false; // older than |N|
      }
      return true;
    });
  }, [rows, search, amFilter, healthFilter, projectsFilter, ageFilter]);

  const anyFilter = search || amFilter || healthFilter || projectsFilter || ageFilter;
  const clearAll = () => {
    setSearch(''); setAmFilter(''); setHealthFilter(''); setProjectsFilter(''); setAgeFilter('');
  };

  // Reps who've touched the account other than the most recent one.
  // Shown as a secondary "Also: ..." line so multi-rep coverage is visible
  // from the list. Click drills into the account's activity log.
  const otherReps = (a) => (a.rep_names || []).filter(n => n && n !== a.last_contact_rep_name);

  return (
    <>
      <h1 style={styles.h1}>Accounts <span style={{ color: '#9CA3AF', fontWeight: 400, fontSize: 14 }}>· {filtered.length}{anyFilter && filtered.length !== rows.length ? ` of ${rows.length}` : ''}</span></h1>

      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          style={{ ...styles.input, maxWidth: 320, flex: '1 1 220px' }}
          placeholder="🔍  Filter by name..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        {showAmFilter && (
          <select style={styles.input} value={amFilter} onChange={e => setAmFilter(e.target.value)} title="Account Manager">
            <option value="">All Account Managers</option>
            {amOptions.map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        )}
        <select style={styles.input} value={healthFilter} onChange={e => setHealthFilter(e.target.value)} title="Health">
          <option value="">All Health</option>
          <option value="active">✓ Active only</option>
          <option value="stale">🌵 Stale only</option>
        </select>
        <select style={styles.input} value={projectsFilter} onChange={e => setProjectsFilter(e.target.value)} title="Projects">
          <option value="">All</option>
          <option value="with">With projects</option>
          <option value="without">No projects</option>
        </select>
        <select style={styles.input} value={ageFilter} onChange={e => setAgeFilter(e.target.value)} title="Last contact">
          <option value="">Any last contact</option>
          <option value="30">Within 30 days</option>
          <option value="60">Within 60 days</option>
          <option value="90">Within 90 days</option>
          <option value="-90">Older than 90 days</option>
        </select>
        {anyFilter && <button style={styles.btnSecondary} onClick={clearAll}>Clear all</button>}
      </div>

      {error && <div style={styles.errorBox}>{error}</div>}

      {isMobile ? (
        <div>
          {filtered.length === 0 && (
            <div style={mobile.emptyHint}>
              {rows.length === 0 ? 'No accounts yet.' : 'No matches.'}
            </div>
          )}
          {filtered.map(a => {
            const cardStyle = a.stale
              ? { ...mobile.listItem, background: '#FEF7E6', borderColor: '#FCD34D' }
              : mobile.listItem;
            return (
              <div key={a.name} style={cardStyle}>
                <div style={mobile.listItemHeader}>
                  <Link to={`/accounts/${encodeURIComponent(a.name)}`} style={{ ...mobile.listItemTitle, color: '#E36F2C', textDecoration: 'none' }}>
                    {a.name}
                  </Link>
                  {a.stale ? (
                    <span title="No call log, project edit, or recent work in the last 60 days" style={{
                      fontSize: 11, fontWeight: 700, color: '#92400E',
                      background: '#FEF3C7', padding: '4px 10px', borderRadius: 10, whiteSpace: 'nowrap'
                    }}>🌵 STALE</span>
                  ) : (
                    <span style={{ color: '#10B981', fontSize: 18 }} title="Active">✓</span>
                  )}
                </div>
                <div style={mobile.listRow}>
                  <span style={mobile.listLabel}>Last Contact</span>
                  <span style={mobile.listValue}>{fmtDateTime(a.last_contact_at) || '—'}</span>
                </div>
                <div style={{ ...mobile.listRow, flexDirection: 'column', alignItems: 'stretch' }}>
                  <span style={mobile.listLabel}>Account Manager</span>
                  <Link to={`/accounts/${encodeURIComponent(a.name)}`} style={{ ...mobile.listValue, color: '#E36F2C', textDecoration: 'none', fontWeight: 600 }}>
                    {a.last_contact_rep_name || '—'}
                  </Link>
                  {otherReps(a).length > 0 && (
                    <span style={{ fontSize: 12, color: '#6B7280', marginTop: 2 }}>
                      Also: {otherReps(a).join(', ')}
                    </span>
                  )}
                </div>
                <div style={mobile.listRow}>
                  <span style={mobile.listLabel}>Entries · Projects</span>
                  <span style={mobile.listValue}>{a.entry_count} · {a.project_count}</span>
                </div>
                <div style={mobile.listRow}>
                  <span style={mobile.listLabel}>Pipeline $</span>
                  <span style={mobile.listValue}>{fmtMoney(a.pipeline_value)}</span>
                </div>
                <div style={mobile.listRow}>
                  <span style={mobile.listLabel}>Won $</span>
                  <span style={mobile.listValue}>{fmtMoney(a.won_value)}</span>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
      <div style={styles.card}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Name</th>
              <th style={styles.th}>Last Contact</th>
              <th style={styles.th}>Account Manager</th>
              <th style={styles.th}>Entries</th>
              <th style={styles.th}>Projects</th>
              <th style={styles.th}>Pipeline $</th>
              <th style={styles.th}>Won $</th>
              <th style={styles.th}>Health</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={8} style={{ ...styles.td, textAlign: 'center', color: '#6B7280' }}>
                {rows.length === 0 ? 'No accounts yet.' : 'No matches.'}
              </td></tr>
            )}
            {filtered.map(a => (
              <tr key={a.name} style={a.stale ? { background: '#FEF7E6' } : {}}>
                <td style={styles.td}>
                  <Link to={`/accounts/${encodeURIComponent(a.name)}`} style={{ color: '#E36F2C', fontWeight: 600, textDecoration: 'none' }}>
                    {a.name}
                  </Link>
                </td>
                <td style={styles.td}>{fmtDateTime(a.last_contact_at) || '—'}</td>
                <td style={styles.td}>
                  {a.last_contact_rep_name ? (
                    <Link to={`/accounts/${encodeURIComponent(a.name)}`} style={{ color: '#1F2937', fontWeight: 600, textDecoration: 'none' }}>
                      {a.last_contact_rep_name}
                    </Link>
                  ) : '—'}
                  {otherReps(a).length > 0 && (
                    <div style={{ fontSize: 12, color: '#6B7280', marginTop: 2 }}>
                      Also: {otherReps(a).join(', ')}
                    </div>
                  )}
                </td>
                <td style={styles.td}>{a.entry_count}</td>
                <td style={styles.td}>{a.project_count}</td>
                <td style={styles.td}>{fmtMoney(a.pipeline_value)}</td>
                <td style={styles.td}>{fmtMoney(a.won_value)}</td>
                <td style={styles.td}>
                  {a.stale ? (
                    <span title="No call log, project edit, or recent work in the last 60 days" style={{
                      fontSize: 11, fontWeight: 700, color: '#92400E',
                      background: '#FEF3C7', padding: '2px 8px', borderRadius: 8
                    }}>🌵 STALE</span>
                  ) : (
                    <span style={{ color: '#10B981', fontSize: 12 }}>✓</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}
    </>
  );
}
